import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import AssetTypeSelector from './AssetTypeSelector';
import AssetFormContainer from './AssetFormContainer';
import AssetPreview from './AssetPreview';
import { getAssetTypeConfig, generateFileName } from './utils/assetTypeConfig';
import { validateForm } from './utils/validation';

interface AssetCreationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: number;
  onSuccess: () => void;
  apiBaseUrl: string;
  clientId?: number;
}

export default function AssetCreationWizard({
  isOpen,
  onClose,
  contentId,
  onSuccess,
  apiBaseUrl,
  clientId
}: AssetCreationWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState<string>('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const assetConfig = selectedType ? getAssetTypeConfig(selectedType) : null;
  const totalSteps = 3;

  const handleTypeSelect = (typeId: string) => {
    console.log('Asset type selected:', typeId);
    setSelectedType(typeId);
    setFormData({});
    setErrors({});
    setCurrentStep(2);
    console.log('Moving to step 2');
  };

  const handleFormChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear error for this field
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  const handleNext = () => {
    if (currentStep === 2 && assetConfig) {
      // Validate form before proceeding
      const validationErrors = validateForm(assetConfig.fields, formData);
      if (validationErrors.length > 0) {
        const errorMap: Record<string, string> = {};
        validationErrors.forEach(error => {
          errorMap[error.field] = error.message;
        });
        setErrors(errorMap);
        return;
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!assetConfig) return;

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('content_id', contentId.toString());
      formDataToSend.append('asset_type', selectedType);

      // Handle file upload or text content
      if (assetConfig.requiresUpload && formData.file) {
        formDataToSend.append('file', formData.file);

        // Add metadata for file uploads
        const metadata: Record<string, any> = {};
        for (const [key, value] of Object.entries(formData)) {
          if (key !== 'file') {
            metadata[key] = value;
          }
        }
        if (Object.keys(metadata).length > 0) {
          formDataToSend.append('metadata', JSON.stringify(metadata));
        }
      } else {
        // For text-based assets
        const content = formData.content || formData.html_content || formData.post_text || '';
        const metadata: Record<string, any> = {};

        // Separate content from metadata
        for (const [key, value] of Object.entries(formData)) {
          if (!['content', 'html_content', 'post_text'].includes(key)) {
            metadata[key] = value;
          }
        }

        // Special handling for different asset types
        if (selectedType === 'email') {
          formDataToSend.append('content', formData.html_content || '');
          metadata.subject = formData.subject;
          metadata.preheader = formData.preheader;
          metadata.from_name = formData.from_name;
          metadata.from_email = formData.from_email;
          metadata.reply_to = formData.reply_to;
          metadata.plain_text = formData.plain_text;
          metadata.utm_source = formData.utm_source;
          metadata.utm_medium = formData.utm_medium;
          metadata.utm_campaign = formData.utm_campaign;
        } else if (selectedType === 'social') {
          formDataToSend.append('content', formData.post_text || '');
          metadata.platform = formData.platform;
          metadata.hashtags = formData.hashtags;
          metadata.mentions = formData.mentions;
          metadata.link = formData.link;
          metadata.cta = formData.cta;
          metadata.schedule = {
            date: formData.schedule_date,
            time: formData.schedule_time
          };
        } else if (selectedType === 'meta') {
          const metaTags = {
            title: formData.title,
            description: formData.description,
            keywords: formData.keywords,
            canonical_url: formData.canonical_url,
            og_tags: {
              title: formData.og_title,
              description: formData.og_description,
              image: formData.og_image,
              type: 'website'
            },
            twitter_card: {
              card: 'summary_large_image',
              title: formData.og_title || formData.title,
              description: formData.og_description || formData.description,
              image: formData.og_image
            },
            robots: formData.robots,
            page_url: formData.page_url
          };
          formDataToSend.append('content', JSON.stringify(metaTags));
        } else if (selectedType === 'faq') {
          const faqContent = {
            category: formData.category,
            questions: formData.questions ? JSON.parse(formData.questions) : [],
            schema_markup: formData.schema_markup === 'yes',
            format: formData.format
          };
          formDataToSend.append('content', JSON.stringify(faqContent));
        } else if (selectedType === 'ads') {
          const adConfig = {
            campaign_name: formData.campaign_name,
            campaign_type: formData.campaign_type,
            ad_groups: [{
              headlines: formData.headlines,
              descriptions: formData.descriptions,
              display_url: formData.display_url,
              final_url: formData.final_url,
              keywords: formData.keywords
            }],
            budget: {
              daily: formData.daily_budget
            }
          };
          formDataToSend.append('content', JSON.stringify(adConfig));
        } else {
          // Default text content
          formDataToSend.append('content', content);
        }

        // Add metadata if any
        if (Object.keys(metadata).length > 0) {
          formDataToSend.append('metadata', JSON.stringify(metadata));
        }

        // Generate file name
        const fileName = generateFileName(selectedType, formData.title);
        formDataToSend.append('file_name', fileName);
      }

      // Submit to API
      await axios.post(`${apiBaseUrl}/api/assets/upload`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Success
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to create asset:', error);
      alert('Failed to create asset. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedType('');
    setFormData({});
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div className="relative z-50 flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg w-[1200px] max-w-[95vw] max-h-[90vh] flex flex-col"
        >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create New Asset</h2>
            <p className="text-sm text-gray-600 mt-1">
              Step {currentStep} of {totalSteps}
              {currentStep === 1 && ' - Choose Asset Type'}
              {currentStep === 2 && ' - Fill Details'}
              {currentStep === 3 && ' - Preview & Confirm'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map(step => (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  step < currentStep ? 'bg-green-500 text-white' :
                  step === currentStep ? 'bg-blue-500 text-white' :
                  'bg-gray-200 text-gray-600'
                }`}>
                  {step < currentStep ? <Check className="w-4 h-4" /> : step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 rounded ${
                    step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentStep === 1 && (
            <AssetTypeSelector
              onSelect={handleTypeSelect}
              selectedType={selectedType}
            />
          )}

          {currentStep === 2 && assetConfig && (
            <AssetFormContainer
              assetType={selectedType}
              config={assetConfig}
              formData={formData}
              errors={errors}
              onChange={handleFormChange}
              clientId={clientId}
            />
          )}

          {currentStep === 3 && assetConfig && (
            <AssetPreview
              assetType={selectedType}
              config={assetConfig}
              formData={formData}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-md ${
              currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Create Asset
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}